/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, XAxis, YAxis,
} from 'recharts';
import { themeColors } from '../../Charts/common';
import { chartsConfig } from './config';
import { BaseLegend } from './Legend';

interface BaseBarChartProps<T extends Record<string, unknown>> {
  mainAxisDataKey: keyof T;
  data: T[];
  yAxisUnit?:string;
  xAxisUnit?: string;
  // config?: BaseChartConfig; // Not used in this version
  height?: number;
  width?: number;
  yAxisDomain?: [number | string, number | string];
  xAxisDomain?: [number | string, number | string];
  legendLabels: Partial<Record<keyof T, string>>;
  dataKeys: Array<keyof T>;
  margin?:{
    top: number;
    right: number;
    left: number;
    bottom: number;
  },
  layout: 'horizontal' | 'vertical';
}

export function BaseComparissonBarChart<T extends Record<string, unknown>>(
  props: BaseBarChartProps<T>
) {
  const {
    layout = 'horizontal',
    yAxisUnit,
    xAxisUnit,
    yAxisDomain,
    xAxisDomain,
    data,
    height,
    width,
    mainAxisDataKey,
    legendLabels,
    dataKeys,
    margin = {
      top: 0,
      right: 0,
      left: -10,
      bottom: 0,
    },
  } = props;

  // Determine axis types and positions based on layout
  const xAxisType = layout === 'horizontal' ? 'category' : 'number';
  const yAxisType = layout === 'horizontal' ? 'number' : 'category';
  // Helps to preserve categorical axis sufficient space to display labels
  const categoryAxisWidth = layout === 'vertical' ? 123 : undefined;

  // Default color array if needed
  const defaultColors = [themeColors.info, themeColors.secondary];

  // Difference between two data keys
  const getDiffLabel = (category: string | number) => {
    if (dataKeys.length === 2) {
      const [key1, key2] = dataKeys;
      const item = data.find((d) => d[mainAxisDataKey] === category);
      if (item && typeof item[key1] === 'number' && typeof item[key2] === 'number') {
        const diff = ((item[key1] as number) - (item[key2] as number)).toPrecision(2);
        return `${diff}`;
      }
    }
    return '';
  };

  return (
    <BarChart
      layout={layout}
      data={data}
      margin={margin}
      width={width}
      height={height ?? 250}
      barGap="160px"
    >
      <CartesianGrid strokeDasharray="3 3" vertical={layout === 'vertical'} horizontal={layout === 'horizontal'} />
      <XAxis
        type={xAxisType}
        domain={layout === 'horizontal' ? xAxisDomain : yAxisDomain}
        tick={{ fontSize: chartsConfig.tick.fontSize }}
        dataKey={layout === 'horizontal' ? mainAxisDataKey as string : undefined}
        axisLine={layout === 'horizontal'}
        unit={layout === 'horizontal' ? xAxisUnit : yAxisUnit}
        tickFormatter={layout === 'horizontal' ? (value: string | number) => `${value}(${getDiffLabel(value)})` : undefined}
      />
      <YAxis
        type={yAxisType}
        tick={{ fontSize: chartsConfig.tick.fontSize }}
        domain={layout === 'vertical' ? yAxisDomain : xAxisDomain}
        dataKey={layout === 'vertical' ? mainAxisDataKey as string : undefined}
        width={categoryAxisWidth}
        axisLine={layout === 'vertical'}
        unit={layout === 'vertical' ? xAxisUnit : yAxisUnit}
        tickFormatter={layout === 'vertical' ? (value: string | number) => `${value}(${getDiffLabel(value)}${yAxisUnit ?? ''})` : undefined}
      />
      {
        dataKeys.map((key, index) => (
          <Bar
            key={String(key)}
            dataKey={String(key)}
            fill={defaultColors[index % defaultColors.length]}
            isAnimationActive={false}
          >
            <LabelList
              dataKey={key as string}
              fontSize={12}
              fill="#000"
              position="right"
              formatter={(value: number | string) => (yAxisUnit ? `${String(value)} ${yAxisUnit}` : String(value))}
            />
          </Bar>
        ))
      }

      <Legend
        className="w-100"
        content={(legProps) => (
          <BaseLegend
            payload={legProps?.payload ?? []}
            legendLabels={legendLabels}
          />
        )}
      />
    </BarChart>
  );
}
